import { Building2, Landmark, MessageCircle, CheckCircle, Bot, MapPin } from "lucide-react";
import mapaCobertura from "@/assets/mapa-cobertura-brasil.png";
import dist01 from "@/assets/distribuidoras/dist-01.png";
import dist02 from "@/assets/distribuidoras/dist-02.png";
import dist03 from "@/assets/distribuidoras/dist-03.png";
import dist04 from "@/assets/distribuidoras/dist-04.png";
import dist05 from "@/assets/distribuidoras/dist-05.png";
import dist06 from "@/assets/distribuidoras/dist-06.png";
import dist07 from "@/assets/distribuidoras/dist-07.png";
import dist08 from "@/assets/distribuidoras/dist-08.png";
import dist09 from "@/assets/distribuidoras/dist-09.png";
import dist10 from "@/assets/distribuidoras/dist-10.png";
import dist11 from "@/assets/distribuidoras/dist-11.png";
import dist12 from "@/assets/distribuidoras/dist-12.png";
import { Layout } from "@/components/layout/Layout";
import { PageHero } from "@/components/shared/PageHero";
import { CTASection } from "@/components/shared/CTASection";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { SEO } from "@/components/seo/SEO";

const distribuidoraNames = [
  "Energisa", "Neoenergia", "Equatorial", "Enel", "CPFL", "Copel",
  "Cemig", "Celesc", "EDP", "Light", "CEE", "RGE"
];

const distribuidoraLogos = [
  dist01, dist02, dist03, dist04, dist05, dist06,
  dist07, dist08, dist09, dist10, dist11, dist12,
];

const integrationCategories = [
  {
    icon: Building2,
    title: "Distribuidoras de Energia",
    description: "Robôs homologados para download automático de faturas nos portais das distribuidoras. Cobertura em 19 estados.",
    color: "from-blue-500 to-cyan-500",
    integrations: [
      ...distribuidoraNames.map(name => ({ name, status: "Disponível" })),
      { name: "Outras", status: "Sob consulta" },
    ],
  },
  {
    icon: Landmark,
    title: "Bancos e Pagamentos",
    description: "Emissão de boletos e PIX integrada com instituições financeiras homologadas.",
    color: "from-emerald-500 to-teal-500",
    integrations: [
      { name: "ASAAS", status: "Disponível" },
      { name: "Banco do Brasil", status: "Disponível" },
      { name: "Cora", status: "Disponível" },
      { name: "Inter", status: "Disponível" },
      { name: "Santander", status: "Disponível" },
      { name: "Itaú", status: "Disponível" },
      { name: "Sicredi", status: "Disponível" },
      { name: "SICOOB", status: "Disponível" },
      { name: "PIX (via integração)", status: "Disponível" },
    ],
  },
  {
    icon: Bot,
    title: "Robôs de Download",
    description: "Automação para coleta de faturas via portal, e-mail ou upload manual, com gestão de status detalhada.",
    color: "from-violet-500 to-purple-500",
    integrations: [
      { name: "Download automático (portais)", status: "Disponível" },
      { name: "Coleta via e-mail", status: "Disponível" },
      { name: "Gestão de status (quarentena)", status: "Disponível" },
      { name: "Tratamento de erros e campos", status: "Disponível" },
      { name: "Upload manual (formulário)", status: "Disponível" },
    ],
  },
  {
    icon: MessageCircle,
    title: "Comunicação",
    description: "Notificações automáticas para consumidores e gestores via múltiplos canais.",
    color: "from-orange-500 to-amber-500",
    integrations: [
      { name: "WhatsApp Business API", status: "Disponível" },
      { name: "E-mail (SMTP/API)", status: "Disponível" },
      { name: "SMS", status: "Disponível" },
      { name: "Notificações push", status: "Em breve" },
    ],
  },
];

export default function Integracoes() {
  return (
    <Layout>
      <SEO path="/integracoes" title="Integrações — Cogni" description="Integrações com distribuidoras de energia e instituições bancárias para automatizar a operação de GD." />
      <PageHero
        title="Integrações que conectam toda sua operação"
        subtitle="A Cogni se integra com distribuidoras, bancos e ferramentas de comunicação para automatizar o ciclo de faturamento de ponta a ponta. Robôs de download em 19 estados."
      />

      {/* Distribuidoras com cobertura */}
      <section className="py-8 sm:py-12 border-b border-border bg-muted/30">
        <div className="container-wide">
          <div className="text-center mb-6">
            <div className="flex items-center justify-center gap-2 mb-2">
              <MapPin className="h-5 w-5 text-primary" />
              <h3 className="font-semibold text-sm sm:text-base">Cobertura em 19 estados</h3>
            </div>
            <p className="text-xs sm:text-sm text-muted-foreground">Distribuidoras atendidas com robôs de download automático</p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 max-w-4xl mx-auto">
            {distribuidoraLogos.map((logo, i) => (
              <div key={i} className="h-8 sm:h-10 flex items-center justify-center">
                <img src={logo} alt="Logo distribuidora" className="h-full w-auto object-contain opacity-70 hover:opacity-100 transition-opacity" />
              </div>
            ))}
          </div>
          <div className="mt-6 text-center">
            <img src={mapaCobertura} alt="Mapa de cobertura das distribuidoras atendidas pela Cogni no Brasil" className="max-w-xs sm:max-w-sm md:max-w-md mx-auto" />
          </div>
        </div>
      </section>

      <section className="section-padding section-alt">
        <div className="container-wide">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-8">
            {integrationCategories.map((category, index) => (
              <Card key={index} className="border-border shadow-lg hover:shadow-xl transition-shadow overflow-hidden">
                <CardHeader className={`bg-gradient-to-r ${category.color} text-white p-4 sm:p-6`}>
                  <div className="flex items-center gap-3 sm:gap-4">
                    <div className="w-11 h-11 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-white/20 flex items-center justify-center flex-shrink-0">
                      <category.icon className="h-5 w-5 sm:h-7 sm:w-7 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-lg sm:text-xl text-white">{category.title}</CardTitle>
                      <CardDescription className="text-white/80 text-xs sm:text-sm">
                        {category.description}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-4 sm:p-6">
                  <div className="grid grid-cols-1 xs:grid-cols-2 gap-2 sm:gap-3">
                    {category.integrations.map((integration, integrationIndex) => (
                      <div
                        key={integrationIndex}
                        className="flex items-center justify-between gap-2 p-2.5 sm:p-3 bg-muted/50 rounded-lg sm:rounded-xl border border-border"
                      >
                        <div className="flex items-center gap-1.5 sm:gap-2">
                          <CheckCircle className={`h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0 ${
                            integration.status === "Disponível" 
                              ? "text-green-500" 
                              : integration.status === "Em breve"
                              ? "text-amber-500"
                              : "text-muted-foreground"
                          }`} />
                          <span className="text-xs sm:text-sm font-medium">{integration.name}</span>
                        </div>
                        <span className={`text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full font-medium flex-shrink-0 ${
                          integration.status === "Disponível"
                            ? "bg-green-100 text-green-700"
                            : integration.status === "Em breve"
                            ? "bg-amber-100 text-amber-700"
                            : "bg-muted text-muted-foreground"
                        }`}>
                          {integration.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* API Section */}
      <section className="section-padding section-dark">
        <div className="container-narrow text-center">
          <h2 className="text-headline font-bold mb-4 text-primary-foreground">
            API aberta para integrações customizadas
          </h2>
          <p className="text-body-lg text-primary-foreground/80 mb-6 sm:mb-8 max-w-2xl mx-auto">
            Precisa integrar com sistemas internos ou ferramentas específicas? 
            Nossa API RESTful permite criar qualquer integração que você precise.
          </p>
          <div className="bg-background rounded-xl sm:rounded-2xl border border-border p-4 sm:p-6 md:p-8 text-left max-w-2xl mx-auto shadow-xl">
            <pre className="text-xs sm:text-sm overflow-x-auto">
              <code className="text-muted-foreground">
{`// Exemplo: Listar UCs de uma usina
GET /api/v1/usinas/:usinaId/ucs

// Resposta
{
  "ucs": [
    {
      "id": "uc_123",
      "numero": "12345678",
      "titular": "João Silva",
      "creditos_mes": 450.5
    }
  ]
}`}
              </code>
            </pre>
          </div>
        </div>
      </section>

      <CTASection
        title="Precisa de uma integração específica?"
        subtitle="Fale com nosso time técnico e veja como podemos conectar a Cogni ao seu sistema."
        primaryCTA="Falar sobre integrações"
      />
    </Layout>
  );
}
